import {
  B2G_CONFIGURATION_ROLES,
  B2G_INTEGRATION_ROUTE_ROLES,
  canAccessB2gIntegration,
  canConfigureB2gIntegration,
  canViewB2gData,
} from './b2g-access';

export type GovernmentIntegrationRailKey =
  | 'dhda-b2g'
  | 'services-australia-aged-care'
  | 'services-australia-hi'
  | 'xero';

export const SERVICES_AUSTRALIA_INTEGRATION_ROLES = [
  'OWNER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'COORDINATOR',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'FINANCE_OFFICER',
] as const;

// This is the exact browser-facing intersection of the legacy proposal and
// decision RPC role cohorts. Quality/compliance roles retain read access to the
// integration rail but cannot be offered a match mutation the database rejects.
export const SERVICES_AUSTRALIA_CARE_RECIPIENT_MATCH_ROLES = [
  'OWNER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'COORDINATOR',
  'FINANCE_OFFICER',
] as const;

export const SERVICES_AUSTRALIA_FUNDING_REVIEW_READ_ROLES = [
  'OWNER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CASE_MANAGER',
  'FINANCE_OFFICER',
] as const;

export const SERVICES_AUSTRALIA_FUNDING_REVIEW_MANAGE_ROLES = [
  'OWNER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'COORDINATOR',
  'FINANCE_OFFICER',
] as const;

export const SERVICES_AUSTRALIA_SAH_BUDGET_SUMMARY_READ_ROLES = [
  'OWNER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CASE_MANAGER',
  'FINANCE_OFFICER',
] as const;

export const SERVICES_AUSTRALIA_SAH_BUDGET_WRITE_STATE_READ_ROLES = [
  'OWNER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'FINANCE_OFFICER',
] as const;

// Deliberately narrower than the generic write-review checker RPC: the client
// budget workbench exposes decisions only to roles that can also reconcile the
// protected budget source.
export const SERVICES_AUSTRALIA_SAH_BUDGET_DECISION_ROLES = [
  'OWNER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'COORDINATOR',
  'FINANCE_OFFICER',
] as const;

export const SERVICES_AUSTRALIA_FUNDING_BUDGET_RPC_ROLES = {
  list_sa_aged_care_rest_funding_reviews_controlled: SERVICES_AUSTRALIA_FUNDING_REVIEW_READ_ROLES,
  prepare_sa_aged_care_rest_funding_review_controlled:
    SERVICES_AUSTRALIA_FUNDING_REVIEW_MANAGE_ROLES,
  decide_sa_aged_care_rest_funding_review_controlled:
    SERVICES_AUSTRALIA_FUNDING_REVIEW_MANAGE_ROLES,
  get_sa_aged_care_rest_funding_summaries_controlled:
    SERVICES_AUSTRALIA_SAH_BUDGET_SUMMARY_READ_ROLES,
  list_sa_aged_care_rest_budget_write_state_controlled:
    SERVICES_AUSTRALIA_SAH_BUDGET_WRITE_STATE_READ_ROLES,
} as const;

export type ServicesAustraliaFundingBudgetRpcName =
  keyof typeof SERVICES_AUSTRALIA_FUNDING_BUDGET_RPC_ROLES;

export type ServicesAustraliaFundingBudgetRpcRole<
  RpcName extends ServicesAustraliaFundingBudgetRpcName,
> = (typeof SERVICES_AUSTRALIA_FUNDING_BUDGET_RPC_ROLES)[RpcName][number];

/** Organisation administrators who may configure provider connections (H10/B13). */
export const GOVERNMENT_PROVIDER_SETUP_CONFIG_ROLES = [
  'OWNER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
] as const;

/**
 * Operational B2G accounts (FNDN-022/160). These roles run RN/QI and reviewed
 * read workflows from compliance surfaces — not privileged configuration accounts.
 */
export const GOVERNMENT_B2G_OPERATIONAL_ROLES = ['QUALITY_OFFICER', 'COMPLIANCE_OFFICER'] as const;

export const HI_INTEGRATION_SETUP_ROLES = GOVERNMENT_PROVIDER_SETUP_CONFIG_ROLES;

export const DHDA_INTEGRATION_SETUP_ROLES = B2G_CONFIGURATION_ROLES;

/** Read access to government integration rails and hub cards. */
export const DHDA_INTEGRATION_ROLES = [...B2G_INTEGRATION_ROUTE_ROLES] as const;

export const HI_INTEGRATION_ROLES = [...GOVERNMENT_PROVIDER_SETUP_CONFIG_ROLES] as const;

export const XERO_INTEGRATION_ROLES = [
  'OWNER',
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
] as const;

export const GOVERNMENT_INTEGRATION_RAIL_ROLES = {
  'services-australia-aged-care': SERVICES_AUSTRALIA_INTEGRATION_ROLES,
  'services-australia-hi': HI_INTEGRATION_ROLES,
  'dhda-b2g': DHDA_INTEGRATION_ROLES,
  xero: XERO_INTEGRATION_ROLES,
} as const satisfies Record<GovernmentIntegrationRailKey, readonly string[]>;

export const GOVERNMENT_INTEGRATION_SETTINGS_ROLES = Array.from(
  new Set(Object.values(GOVERNMENT_INTEGRATION_RAIL_ROLES).flatMap((roles) => [...roles])),
);

export function canAccessGovernmentIntegrationRail(
  role: string,
  rail: GovernmentIntegrationRailKey,
): boolean {
  if (rail === 'dhda-b2g') return canAccessB2gIntegration(role);
  return (GOVERNMENT_INTEGRATION_RAIL_ROLES[rail] as readonly string[]).includes(role);
}

export function canConfigureGovernmentProviderSetup(role: string): boolean {
  return (GOVERNMENT_PROVIDER_SETUP_CONFIG_ROLES as readonly string[]).includes(role);
}

export function canConfigureHiProviderSetup(role: string): boolean {
  return (HI_INTEGRATION_SETUP_ROLES as readonly string[]).includes(role);
}

export function canConfigureB2gProviderSetup(role: string): boolean {
  return canConfigureB2gIntegration(role);
}

/** Privileged configuration accounts must not operate B2G APIs (B03). */
export function canOperateB2gGovernmentApis(role: string): boolean {
  return canViewB2gData(role);
}

export function canAccessGovernmentIntegrationSettings(role: string): boolean {
  return (GOVERNMENT_INTEGRATION_SETTINGS_ROLES as readonly string[]).includes(role);
}

export function canManageServicesAustraliaCareRecipientMatch(role: string): boolean {
  return (SERVICES_AUSTRALIA_CARE_RECIPIENT_MATCH_ROLES as readonly string[]).includes(role);
}

export function canCallServicesAustraliaFundingBudgetRpc<
  RpcName extends ServicesAustraliaFundingBudgetRpcName,
>(role: string, rpcName: RpcName): role is ServicesAustraliaFundingBudgetRpcRole<RpcName> {
  return (SERVICES_AUSTRALIA_FUNDING_BUDGET_RPC_ROLES[rpcName] as readonly string[]).includes(role);
}

export function canReadServicesAustraliaFundingReviews(role: string): boolean {
  return canCallServicesAustraliaFundingBudgetRpc(
    role,
    'list_sa_aged_care_rest_funding_reviews_controlled',
  );
}

export function canManageServicesAustraliaFundingReviews(role: string): boolean {
  return (
    canCallServicesAustraliaFundingBudgetRpc(
      role,
      'prepare_sa_aged_care_rest_funding_review_controlled',
    ) &&
    canCallServicesAustraliaFundingBudgetRpc(
      role,
      'decide_sa_aged_care_rest_funding_review_controlled',
    )
  );
}

export function canReadServicesAustraliaSahBudgetSummaries(role: string): boolean {
  return canCallServicesAustraliaFundingBudgetRpc(
    role,
    'get_sa_aged_care_rest_funding_summaries_controlled',
  );
}

export function canReadServicesAustraliaSahBudgetWriteState(role: string): boolean {
  return canCallServicesAustraliaFundingBudgetRpc(
    role,
    'list_sa_aged_care_rest_budget_write_state_controlled',
  );
}

export function canLoadServicesAustraliaSahBudgetState(role: string): boolean {
  return (
    canReadServicesAustraliaSahBudgetSummaries(role) &&
    canReadServicesAustraliaSahBudgetWriteState(role)
  );
}

export function canManageServicesAustraliaSahBudgetDecisions(role: string): boolean {
  return (SERVICES_AUSTRALIA_SAH_BUDGET_DECISION_ROLES as readonly string[]).includes(role);
}
